import React, { useState } from "react";
import { FingerPrintIcon } from "@heroicons/react/24/outline";

const getDomainHostname = (domain) => {
  if (!domain) return "";
  const raw = String(domain).trim();
  if (!raw) return "";
  try {
    const url = raw.startsWith("http://") || raw.startsWith("https://")
      ? new URL(raw)
      : new URL(`https://${raw}`);
    return url.hostname || "";
  } catch (e) {
    return raw.replace(/^https?:\/\//i, "").split("/")[0];
  }
};

const getBrandFaviconUrl = (brand) => {
  const hostname = getDomainHostname(brand?.domain);
  if (!hostname) return "";
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(hostname)}&sz=64`;
};

const getBrandAvatarUrl = (brand) => {
  const creators = Array.isArray(brand?.creators) ? brand.creators : [];
  const creatorAvatar = creators.find((creator) => creator?.profile_image_url)?.profile_image_url;
  if (creatorAvatar) return creatorAvatar;
  return getBrandFaviconUrl(brand);
};

const BrandAvatar = ({ brand, size = "md", fallback = "initial" }) => {
  const [imageError, setImageError] = useState(false);
  const src = imageError ? "" : getBrandAvatarUrl(brand);
  const brandInitial = (brand?.name || "B").trim().charAt(0).toUpperCase() || "B";
  const sizeClass =
    size === "sm" ? "w-4 h-4 text-[10px]" :
    size === "highlight" ? "w-7 h-7 text-[11px]" :
    size === "lg" ? "w-16 h-16 text-xl" :
    "w-10 h-10 text-sm";
  const fingerprintIconClass =
    size === "sm" ? "h-3 w-3" :
    size === "highlight" ? "h-4 w-4" :
    size === "lg" ? "h-8 w-8" :
    "h-5 w-5";

  if (src) {
    return (
      <img
        src={src}
        alt={brand?.name || "Brand"}
        className={`${sizeClass} rounded-full object-cover border border-theme-secondary bg-theme-main flex-shrink-0`}
        onError={() => setImageError(true)}
      />
    );
  }

  if (fallback === "fingerprint") {
    return (
      <div className={`${sizeClass} rounded-full border border-theme-secondary bg-theme-main flex items-center justify-center flex-shrink-0`}>
        <FingerPrintIcon className={`${fingerprintIconClass} text-theme-text opacity-50`} />
      </div>
    );
  }

  return (
    <div className={`${sizeClass} rounded-full border border-theme-secondary bg-theme-main text-theme-text flex items-center justify-center font-semibold flex-shrink-0 leading-none`}>
      {brandInitial}
    </div>
  );
};

const BrandSelector = ({ brands, selectedBrand, onSelectBrand, onAddNewBrand }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center gap-1.5 text-theme-text text-sm hover:opacity-85 cursor-pointer"
      >
        <BrandAvatar brand={selectedBrand} size="sm" />
        <span className="truncate">{selectedBrand?.name || "Select brand"}</span>
        <svg className="w-4 h-4 opacity-60 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open ? (
        <>
          <div className="fixed inset-0 z-10" aria-hidden onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 w-64 py-2 rounded-lg border border-theme-secondary bg-theme-main shadow-lg z-20">
            {brands.map((b) => (
              <button
                key={b.hash}
                type="button"
                onClick={() => {
                  onSelectBrand(b.hash);
                  setOpen(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-theme-text hover:bg-theme-secondary"
              >
                <BrandAvatar brand={b} />
                <span className="truncate">{b.name || "(Unnamed brand)"}</span>
                {b.hash === selectedBrand?.hash ? (
                  <span className="ml-auto text-theme-primary">✓</span>
                ) : null}
              </button>
            ))}
            <div className="border-t border-theme-secondary mt-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onAddNewBrand();
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-theme-primary hover:bg-theme-secondary"
              >
                <span className="w-10 h-10 rounded-full border border-theme-secondary flex items-center justify-center text-lg">+</span>
                Add new brand
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export { BrandAvatar };
export default BrandSelector;
